(function () {
  "use strict";

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function toMap(items) {
    return (items || []).reduce(function (map, item) {
      map[item.key] = item;
      return map;
    }, {});
  }

  function createAttemptId() {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
      return window.crypto.randomUUID();
    }

    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (character) {
      var random = Math.floor(Math.random() * 16);
      var value = character === "x" ? random : (random & 3) | 8;
      return value.toString(16);
    });
  }

  function ConsultationEngine(data, options) {
    if (!data || !Array.isArray(data.journeys)) {
      throw new Error("Biteback consultation data must include journeys.");
    }

    this.data = data;
    this.options = options || {};
    this.eventTarget = this.options.eventTarget || document;
    this.eventContext = this.options.context || {};
    this.journeyMap = toMap(data.journeys);
    this.productMap = toMap(data.productCatalog);
    this.state = this.createEmptyState();
  }

  ConsultationEngine.prototype.createEmptyState = function () {
    return {
      journeyKey: null,
      stepKey: null,
      pathway: [],
      result: null,
      completed: false,
      completionEmitted: false,
      abandoned: false,
      attemptId: null
    };
  };

  ConsultationEngine.prototype.getJourneys = function () {
    return clone(this.data.journeys.map(function (journey) {
      return {
        key: journey.key,
        name: journey.name,
        description: journey.description,
        firstStepKey: journey.firstStepKey
      };
    }));
  };

  ConsultationEngine.prototype.getJourney = function (journeyKey) {
    var journey = this.journeyMap[journeyKey];
    if (!journey) {
      throw new Error("Unknown consultation journey: " + journeyKey);
    }
    return journey;
  };

  ConsultationEngine.prototype.getStepMap = function (journey) {
    return toMap(journey.steps);
  };

  ConsultationEngine.prototype.getRecommendationMap = function (journey) {
    return toMap(journey.recommendations);
  };

  ConsultationEngine.prototype.start = function (journeyKey) {
    var journey = this.getJourney(journeyKey);

    this.state = {
      journeyKey: journey.key,
      stepKey: journey.firstStepKey,
      pathway: [],
      result: null,
      completed: false,
      completionEmitted: false,
      abandoned: false,
      attemptId: createAttemptId()
    };

    this.emit("consultation_started", {
      journey: this.summariseJourney(journey),
      pathway: []
    });

    return this.getCurrentStep();
  };

  ConsultationEngine.prototype.getCurrentStep = function () {
    if (!this.state.journeyKey || this.state.completed) {
      return null;
    }

    var journey = this.getJourney(this.state.journeyKey);
    var step = this.getStepMap(journey)[this.state.stepKey];

    if (!step) {
      throw new Error("Unknown consultation step: " + this.state.stepKey);
    }

    return clone(step);
  };

  ConsultationEngine.prototype.selectChoice = function (choiceKey) {
    var step = this.getCurrentStep();
    if (!step) {
      throw new Error("Cannot select a choice without an active step.");
    }

    var choice = (step.choices || []).find(function (candidate) {
      return candidate.key === choiceKey;
    });

    if (!choice) {
      throw new Error("Unknown consultation choice: " + choiceKey);
    }

    var journey = this.getJourney(this.state.journeyKey);
    var answer = {
      stepKey: step.key,
      stepPrompt: step.prompt,
      choiceKey: choice.key,
      choiceLabel: choice.label
    };

    this.state.pathway.push(answer);

    this.emit("answer_selected", {
      journey: this.summariseJourney(journey),
      answer: clone(answer),
      pathway: clone(this.state.pathway)
    });

    if (choice.recommendationKey) {
      this.complete(choice.recommendationKey);
      return null;
    }

    if (!choice.nextStepKey) {
      throw new Error("Choice must include nextStepKey or recommendationKey: " + choiceKey);
    }

    this.state.stepKey = choice.nextStepKey;
    return this.getCurrentStep();
  };

  ConsultationEngine.prototype.complete = function (recommendationKey) {
    var journey = this.getJourney(this.state.journeyKey);
    var recommendation = this.getRecommendationMap(journey)[recommendationKey];

    if (!recommendation) {
      throw new Error("Unknown consultation recommendation: " + recommendationKey);
    }

    var shouldEmitCompletion = !this.state.completionEmitted;
    this.state.completed = true;
    this.state.completionEmitted = true;
    this.state.abandoned = false;
    this.state.stepKey = null;
    this.state.result = this.buildResult(journey, recommendation);

    if (shouldEmitCompletion) {
      this.emit("consultation_completed", {
        journey: this.summariseJourney(journey),
        recommendationKey: recommendation.key,
        pathway: clone(this.state.pathway),
        result: clone(this.state.result)
      });

      this.recordRecommendationViewed(this.state.result);
    }
  };

  ConsultationEngine.prototype.back = function () {
    if (!this.state.journeyKey) {
      return null;
    }

    if (this.state.pathway.length === 0) {
      var currentJourney = this.getJourney(this.state.journeyKey);
      this.state = this.createEmptyState();

      this.emit("answer_selected", {
        journey: this.summariseJourney(currentJourney),
        action: "back_to_journey_selector",
        pathway: []
      });

      return null;
    }

    var journey = this.getJourney(this.state.journeyKey);
    var previous = this.state.pathway.pop();

    this.state.completed = false;
    this.state.result = null;
    this.state.stepKey = previous.stepKey;

    this.emit("answer_selected", {
      journey: this.summariseJourney(journey),
      action: "back",
      pathway: clone(this.state.pathway)
    });

    return this.getCurrentStep();
  };

  ConsultationEngine.prototype.reset = function (reason) {
    this.abandon(reason);
    this.state = this.createEmptyState();
  };

  ConsultationEngine.prototype.restart = function () {
    var journeyKey = this.state.journeyKey;
    this.reset("start_over");
    return journeyKey ? this.start(journeyKey) : null;
  };

  ConsultationEngine.prototype.abandon = function (reason) {
    if (!this.state.journeyKey || this.state.completed || this.state.completionEmitted || this.state.abandoned) {
      return false;
    }

    this.state.abandoned = true;
    var journey = this.getJourney(this.state.journeyKey);
    this.emit("consultation_abandoned", {
      journey: this.summariseJourney(journey),
      pathway: clone(this.state.pathway),
      reason: reason || null
    });
    return true;
  };

  ConsultationEngine.prototype.getResult = function () {
    return this.state.result ? clone(this.state.result) : null;
  };

  ConsultationEngine.prototype.buildResult = function (journey, recommendation) {
    var self = this;
    return {
      version: this.data.version,
      journey: this.summariseJourney(journey),
      pathway: clone(this.state.pathway),
      recommendationKey: recommendation.key,
      recommendationCopy: recommendation.recommendationCopy,
      humanHelpCopy: recommendation.humanHelpCopy,
      products: recommendation.products.map(function (item) {
        var catalogProduct = self.productMap[item.key] || { key: item.key, title: item.key };
        return Object.assign({}, catalogProduct, item, { order: item.order });
      }),
      humanHelp: {
        label: this.data.widgetDefaults.humanHelpLabel,
        url: this.data.widgetDefaults.humanHelpUrl
      }
    };
  };

  ConsultationEngine.prototype.summariseJourney = function (journey) {
    return {
      key: journey.key,
      name: journey.name
    };
  };

  ConsultationEngine.prototype.recordRecommendationViewed = function (result) {
    this.emit("recommendation_viewed", {
      journey: result.journey,
      recommendationKey: result.recommendationKey,
      pathway: clone(result.pathway),
      products: clone(result.products)
    });
  };

  ConsultationEngine.prototype.recordProductClicked = function (product, context) {
    this.emit("product_clicked", {
      journey: this.state.journeyKey ? this.summariseJourney(this.getJourney(this.state.journeyKey)) : null,
      pathway: clone(this.state.pathway),
      product: clone(product),
      context: context || null
    });
  };

  ConsultationEngine.prototype.recordAddToCart = function (product, context) {
    this.emit("add_to_cart", {
      journey: this.state.journeyKey ? this.summariseJourney(this.getJourney(this.state.journeyKey)) : null,
      pathway: clone(this.state.pathway),
      product: clone(product),
      context: context || null
    });
  };

  ConsultationEngine.prototype.on = function (eventName, handler) {
    this.eventTarget.addEventListener(eventName, handler);
    return function () {
      this.eventTarget.removeEventListener(eventName, handler);
    }.bind(this);
  };

  ConsultationEngine.prototype.emit = function (eventName, detail) {
    var payload = Object.assign(
      {
        version: this.data.version,
        timestamp: new Date().toISOString(),
        attemptId: this.state.attemptId || null
      },
      this.eventContext,
      detail || {}
    );

    if (typeof CustomEvent === "function") {
      this.eventTarget.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
      return;
    }

    var event = document.createEvent("CustomEvent");
    event.initCustomEvent(eventName, false, false, payload);
    this.eventTarget.dispatchEvent(event);
  };

  ConsultationEngine.createDefault = function (options) {
    return new ConsultationEngine(window.BitebackConsultationSeed, options || {});
  };

  window.BitebackConsultationEngine = ConsultationEngine;
})();
